import React, { useEffect, useState } from 'react';

import { styled } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import type { MediaProvider } from '@atlaskit/editor-common/provider-factory';
import WarningIcon from '@atlaskit/icon/core/warning';
import { MediaClientProvider } from '@atlaskit/media-client-react';
import type { MediaClientConfig } from '@atlaskit/media-core';
import { MediaImage } from '@atlaskit/media-image';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { useConfluenceDependencies } from '../../../controllers/use-confluence-dependencies';

import { messages } from './messages';

const errorWrapperStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	height: '100%',
	justifyContent: 'center',
	alignItems: 'center',
	background: token('color.background.accent.gray.subtlest'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ImageWrapper = styled.img({
	height: '100%',
	width: '100%',
	aspectRatio: 'unset',
	objectFit: 'cover',
});

const uploadingImageStyles = xcss({
	width: '100%',
	height: '100%',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	background: token('color.background.accent.gray.subtlest'),
});

type CustomImageUploadProps = {
	/**
	 * imageSrc can be an Unsplash URL or a "collectionId/imageId" string.
	 */
	imageSrc?: string;
	contentId: string;
	imageAltText?: string;
	shouldShowErrorMessage?: boolean;
};

const getImageMediaLocation = (
	activeContentId: string,
	maybeCustomCoverImage: string,
): [string, string] => {
	if (maybeCustomCoverImage.indexOf('/') !== -1) {
		const temp = maybeCustomCoverImage.split('/');
		return [temp[0], temp[1]];
	}
	return [activeContentId, maybeCustomCoverImage];
};

export const CustomImageUploadRenderer = ({
	contentId,
	imageSrc = '',
	imageAltText,
	shouldShowErrorMessage = false,
}: CustomImageUploadProps) => {
	const { experienceSuccessComponent, experienceStopComponent, createRendererMediaProvider } =
		useConfluenceDependencies();
	const intl = useIntl();
	const [collectionId, imageId] = getImageMediaLocation(contentId, imageSrc);
	const mediaConfig = useMediaConfig({ collectionId, createRendererMediaProvider });

	const ExperienceSuccess = experienceSuccessComponent || (() => null);
	const ExperienceError = experienceStopComponent || (() => null);

	if (!mediaConfig || !mediaConfig.authProvider) {
		return null;
	}

	const errorState = (
		<Box xcss={errorWrapperStyles} testId="custom-image-upload-error">
			<WarningIcon
				label={intl.formatMessage(messages.warningIcon)}
				color={token('color.icon.subtle')}
			/>
			{shouldShowErrorMessage && <FormattedMessage {...messages.imageFailedMessage} />}
		</Box>
	);

	return (
		<MediaClientProvider clientConfig={mediaConfig}>
			<MediaImage
				identifier={{
					mediaItemType: 'file',
					id: imageId,
					collectionName: mediaConfig?.collection,
				}}
				// Force rerender image on ID change until https://product-fabric.atlassian.net/browse/CXP-2899 is fixed
				key={imageId}
				mediaClientConfig={{
					authProvider: mediaConfig.authProvider,
				}}
				apiConfig={{
					allowAnimated: true,
					mode: 'fit',
					upscale: false,
				}}
			>
				{({ data, loading, error }) => {
					if (loading) {
						return <Box testId="custom-image-upload-loading" xcss={uploadingImageStyles} />;
					}

					if (error || !data?.src) {
						return (
							<>
								{errorState}
								<ExperienceError
									error={new Error('Image failed to load in CustomImageUploadRenderer')}
								/>
							</>
						);
					}

					return (
						<>
							<ImageWrapper src={data.src} alt={imageAltText} data-testid="custom-image-upload" />
							<ExperienceSuccess />
						</>
					);
				}}
			</MediaImage>
		</MediaClientProvider>
	);
};

interface ExtendedMediaClientConfig extends MediaClientConfig {
	collection: string;
}

function useMediaConfig({
	collectionId,
	createRendererMediaProvider,
}: {
	collectionId: string;
	createRendererMediaProvider?: (options: {
		contentId: string;
		isExternalShare: boolean;
	}) => Promise<MediaProvider>;
}): ExtendedMediaClientConfig | null {
	const [mediaConfig, setMediaConfig] = useState<ExtendedMediaClientConfig | null>(null);

	useEffect(() => {
		async function createMediaConfig() {
			if (!createRendererMediaProvider) {
				return;
			}

			const { viewMediaClientConfig } = await createRendererMediaProvider({
				contentId: collectionId,
				isExternalShare: false,
			});

			if (!viewMediaClientConfig) {
				setMediaConfig(null);
				return;
			}

			setMediaConfig({
				collection: `contentId-${collectionId}`,
				authProvider: viewMediaClientConfig.authProvider,
			});
		}

		void createMediaConfig();
	}, [collectionId, createRendererMediaProvider]);

	return mediaConfig;
}
